import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "@/context/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Textarea } from "@/components/ui/textarea";
import {
  Globe, Package, ShoppingBag, Eye, EyeOff, Plus, Pencil, Trash2,
  Loader2, Check, ChevronsUpDown, ExternalLink, Copy, AlertTriangle, AlertCircle, X,
  Map as MapIcon, Send, MessageCircle, Truck, Bike, Users, Phone
} from "lucide-react";
import { formatCurrency } from "@/lib/currency";
import { format } from "date-fns";
import FinanceTab from "./ecommerce/finance";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

// --- Types ---

interface OnlineStore {
  id: number; storeId: number; slug: string; displayName: string; tagline: string | null;
  logoUrl: string | null; bannerUrl: string | null; whatsapp: string | null; instagram: string | null;
  address: string | null; pickUpState?: string | null; isPublished: boolean; primaryColor: string; createdAt: string;
  products?: OnlineProduct[];
}

interface OnlineProduct {
  id: number; storeId: number; productId: number | null; name: string; description: string | null; price: string;
  imageUrl: string | null; imageUrls?: string[]; imageVerified?: boolean; category: string | null; stock: number; isAvailable: boolean; createdAt: string;
  shippingWeightKg?: number | null;
}

interface PosProduct {
  id: number; name: string; price: number; stock: number; isService: boolean;
}

interface OnlineRefundItem { orderItemId: number; quantity: number; }
interface OnlineRefund {
  id: number; status: string; source: string; amountKobo: number; platformFeeKobo: number;
  merchantAmountKobo: number; restoreInventory: boolean; providerRefundId: string | null;
  reason: string | null; createdAt: string; items: OnlineRefundItem[];
}

interface OrderItem { id: number; name: string; price: string; quantity: number; canonicalProductId: number | null; }

interface OnlineOrder {
  id: number; orderNumber: string; customerName: string; customerEmail: string; customerPhone: string | null;
  deliveryAddress: string | null; total: string; amountKobo: number | null; grossAmountKobo: number | null;
  fulfillmentType: string | null; deliveryZoneName: string | null; deliveryFeeKobo: number | null; merchandiseAmountKobo: number | null;
  pickupInstructions: string | null;
  platformFeeBps: number; platformFeeKobo: number | null; sellerNetKobo: number | null;
  status: string; paymentStatus: string;
  reservationStatus: string | null; trackingToken: string; notes: string | null; createdAt: string; items: OrderItem[];
  deliveryProvider?: "manual" | "fez" | string | null;
  recipientState?: string | null; weightKg?: number | null; shippingWeightKg?: number | null; quotedFeeKobo?: number | null;
  fezBooking?: { status: string; orderNo: string | null; waybillNumber?: string; providerStatus?: string | null; error?: string | null; recipientState?: string | null; weightKg?: number | null } | null;
  assignment?: any;
  payout?: {
    status: "pending" | "settled" | "failed" | "review";
    providerStatus: string | null;
    expectedAt: string | null;
    settledAt: string | null;
    updatedAt: string | null;
  };
  refunds: OnlineRefund[]; refundedAmountKobo: number; refundedPlatformFeeKobo: number; refundedMerchantAmountKobo: number;
}

const payoutPresentation = {
  pending: { label: "Pending", className: "bg-amber-50 text-amber-800 border-amber-200", detail: "Customer payment is confirmed. Paystack has not yet confirmed the bank settlement." },
  settled: { label: "Settled", className: "bg-emerald-50 text-emerald-700 border-emerald-200", detail: "Paystack confirmed that this order's payout reached settlement." },
  failed: { label: "Failed", className: "bg-red-50 text-red-700 border-red-200", detail: "Paystack reported that this payout failed. Check your Paystack account before relying on these funds." },
  review: { label: "Needs review", className: "bg-orange-50 text-orange-800 border-orange-200", detail: "The provider evidence did not safely match this order. Review the payout in Paystack." },
} as const;

const NIGERIAN_STATES = [
  "Abia", "Adamawa", "Akwa Ibom", "Anambra", "Bauchi", "Bayelsa", "Benue", "Borno",
  "Cross River", "Delta", "Ebonyi", "Edo", "Ekiti", "Enugu", "FCT",
  "Gombe", "Imo", "Jigawa", "Kaduna", "Kano", "Katsina", "Kebbi", "Kogi", "Kwara",
  "Lagos", "Nasarawa", "Niger", "Ogun", "Ondo", "Osun", "Oyo", "Plateau", "Rivers",
  "Sokoto", "Taraba", "Yobe", "Zamfara",
];

const PRODUCT_IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);
const PRODUCT_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
const PRODUCT_IMAGE_MIN_WIDTH = 500;
const PRODUCT_IMAGE_MIN_HEIGHT = 500;
const MAX_PRODUCT_GALLERY_IMAGES = 8;

async function validateProductImage(file: File): Promise<{ width: number; height: number }> {
  if (!PRODUCT_IMAGE_TYPES.has(file.type)) throw new Error("Choose a JPG, PNG or WebP image.");
  if (file.size > PRODUCT_IMAGE_MAX_BYTES) throw new Error("This image is over 5MB. Compress it and try again.");
  const objectUrl = URL.createObjectURL(file);
  try {
    const dimensions = await new Promise<{ width: number; height: number }>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight });
      image.onerror = () => reject(new Error("This image could not be read. Choose another file."));
      image.src = objectUrl;
    });
    if (dimensions.width < PRODUCT_IMAGE_MIN_WIDTH || dimensions.height < PRODUCT_IMAGE_MIN_HEIGHT) {
      throw new Error(`Image is ${dimensions.width}×${dimensions.height}px. Use at least ${PRODUCT_IMAGE_MIN_WIDTH}×${PRODUCT_IMAGE_MIN_HEIGHT}px.`);
    }
    return dimensions;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function uploadImage(uploadURL: string, file: File, onProgress: (progress: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open("PUT", uploadURL);
    request.setRequestHeader("Content-Type", file.type);
    request.upload.onprogress = event => {
      if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100));
    };
    request.onload = () => request.status >= 200 && request.status < 300
      ? resolve()
      : reject(new Error("The upload could not be completed. Try again."));
    request.onerror = () => reject(new Error("The connection was interrupted during upload. Try again."));
    request.send(file);
  });
}

// --- Utils ---

function getOrderTotal(order: OnlineOrder) {
  if (order.amountKobo != null) return order.amountKobo / 100;
  return Number(order.total);
}

function getStatusBadge(status: string) {
  switch (status) {
    case "pending_payment": return <Badge variant="outline" className="text-slate-600 bg-slate-50 border-slate-200 font-medium">Pending Payment</Badge>;
    case "paid": return <Badge variant="outline" className="text-emerald-700 bg-emerald-50 border-emerald-200 font-medium">Paid</Badge>;
    case "confirmed": return <Badge variant="outline" className="text-blue-700 bg-blue-50 border-blue-200 font-medium">Confirmed</Badge>;
    case "preparing": return <Badge variant="outline" className="text-indigo-700 bg-indigo-50 border-indigo-200 font-medium">Preparing</Badge>;
    case "ready_for_pickup": return <Badge variant="outline" className="text-purple-700 bg-purple-50 border-purple-200 font-medium">Ready for Pickup</Badge>;
    case "out_for_delivery": return <Badge variant="outline" className="text-purple-700 bg-purple-50 border-purple-200 font-medium">Out for Delivery</Badge>;
    case "delivered": return <Badge variant="outline" className="text-emerald-700 bg-emerald-50 border-emerald-200 font-medium">Delivered</Badge>;
    case "payment_review": return <Badge variant="outline" className="text-amber-700 bg-amber-50 border-amber-200 font-medium">Manual Review</Badge>;
    case "cancelled": return <Badge variant="outline" className="text-red-700 bg-red-50 border-red-200 font-medium">Cancelled</Badge>;
    case "refunded": return <Badge variant="outline" className="text-slate-700 bg-slate-100 border-slate-300 font-medium">Refunded</Badge>;
    default: return <Badge variant="outline" className="capitalize font-medium">{status.replace(/_/g, ' ')}</Badge>;
  }
}

// --- Tabs ---

function StoreSetupTab({ onSaved }: { onSaved: () => void }) {
  const [store, setStore] = useState<OnlineStore | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [uploadingArtwork, setUploadingArtwork] = useState<"logo" | "banner" | null>(null);
  const [form, setForm] = useState({
    displayName: "", slug: "", tagline: "", whatsapp: "", instagram: "", address: "", pickUpState: "", primaryColor: "#0f172a",
    logoUrl: "", bannerUrl: "",
  });

  const fetch_ = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/api/ecommerce/my-store`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        if (data) {
          setStore(data);
          setForm({
            displayName: data.displayName || "", slug: data.slug || "", tagline: data.tagline || "",
            whatsapp: data.whatsapp || "", instagram: data.instagram || "",
            address: data.address || "", pickUpState: data.pickUpState || "", primaryColor: data.primaryColor || "#0f172a",
            logoUrl: data.logoUrl || "", bannerUrl: data.bannerUrl || "",
          });
        }
      }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch_(); }, [fetch_]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      const res = await fetch(`${BASE}/api/ecommerce/my-store`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "Failed to save"); return; }
      setStore(data); onSaved();
    } catch { setError("Network error"); }
    finally { setSaving(false); }
  };

  const handleArtworkUpload = async (artworkType: "logo" | "banner", file?: File) => {
    if (!file) return;
    setError("");
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setError("Choose a JPG, PNG or WebP image.");
      return;
    }
    if (file.size > PRODUCT_IMAGE_MAX_BYTES) {
      setError("This image is over 5MB. Compress it and try again.");
      return;
    }
    const preview = URL.createObjectURL(file);
    setUploadingArtwork(artworkType);
    try {
      const request = await fetch(`${BASE}/api/ecommerce/my-store/artwork-upload-url`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: file.name, size: file.size, contentType: file.type }),
      });
      const requestData = await request.json().catch(() => ({}));
      if (!request.ok) throw new Error(requestData.error || "Could not prepare this upload.");
      await uploadImage(requestData.uploadURL, file, () => {});
      const finalize = await fetch(`${BASE}/api/ecommerce/my-store/artwork`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ objectPath: requestData.objectPath, artworkType }),
      });
      const finalData = await finalize.json().catch(() => ({}));
      if (!finalize.ok) throw new Error(finalData.error || "The uploaded image could not be verified.");
      setForm(current => ({ ...current, [artworkType === "logo" ? "logoUrl" : "bannerUrl"]: finalData.url }));
      if (artworkType === "logo") setLogoPreview(preview);
      else setBannerPreview(preview);
    } catch (uploadError) {
      URL.revokeObjectURL(preview);
      setError(uploadError instanceof Error ? uploadError.message : "Image upload failed. Try again.");
    } finally { setUploadingArtwork(null); }
  };

  const artworkInput = (artworkType: "logo" | "banner", title: string, storedUrl: string, previewUrl: string | null) => (
    <div className="space-y-2">
      <label htmlFor={`store-${artworkType}-upload`} className="text-sm font-medium">{title}</label>
      {(previewUrl || storedUrl) && (
        <img src={previewUrl || storedUrl} alt={`${title} preview`} className={artworkType === "logo" ? "h-20 w-20 rounded-xl border object-cover" : "h-28 w-full rounded-lg border object-cover"} />
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Input id={`store-${artworkType}-upload`} type="file" accept="image/jpeg,image/png,image/webp" className="max-w-full sm:max-w-sm" disabled={uploadingArtwork !== null || saving}
          aria-describedby={`store-${artworkType}-hint`}
          onChange={event => { void handleArtworkUpload(artworkType, event.currentTarget.files?.[0]); event.currentTarget.value = ""; }} />
        {storedUrl && <Button type="button" variant="outline" size="sm" disabled={saving || uploadingArtwork !== null} onClick={() => {
          setForm(current => ({ ...current, [artworkType === "logo" ? "logoUrl" : "bannerUrl"]: "" }));
          if (artworkType === "logo") setLogoPreview(null); else setBannerPreview(null);
        }}>Remove</Button>}
      </div>
      <p id={`store-${artworkType}-hint`} className="text-xs text-muted-foreground">JPG, PNG or WebP · up to 5MB. Uploads are stored securely in your store space.</p>
      {uploadingArtwork === artworkType && <p role="status" className="text-xs text-muted-foreground">Uploading and verifying image…</p>}
    </div>
  );

  const missingImageCount = store?.products?.filter(product => product.isAvailable && !product.imageVerified).length ?? 0;

  const togglePublish = async (confirmed = false) => {
    if (!store) return;
    if (!store.isPublished && missingImageCount > 0 && !confirmed) {
      const proceed = window.confirm(
        `${missingImageCount} visible product${missingImageCount === 1 ? "" : "s"} still need an image and will not appear to customers. Go live with the remaining products?`,
      );
      if (!proceed) return;
      confirmed = true;
    }
    setPublishing(true);
    setError("");
    try {
      const res = await fetch(`${BASE}/api/ecommerce/my-store/publish`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isPublished: !store.isPublished, confirmImageWarnings: confirmed }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 409 && data.requiresImageConfirmation) {
        const proceed = window.confirm(`${data.error} Go live with the remaining products?`);
        if (proceed) void togglePublish(true);
        return;
      }
      if (!res.ok) { setError(data.error || "Failed to update storefront status"); return; }
      setStore(current => ({ ...data, products: current?.products })); onSaved();
    } catch {
      setError("Network error. Check your connection and try again.");
    } finally { setPublishing(false); }
  };

  const storeUrl = `${window.location.origin}${BASE}/shop/${form.slug}`;

  const copyUrl = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(storeUrl);
      } else {
        const input = document.createElement("textarea");
        input.value = storeUrl;
        input.style.position = "fixed";
        input.style.opacity = "0";
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        input.remove();
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Could not copy the link. Open the store and copy the address from your browser.");
    }
  };

  if (loading) return <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="max-w-3xl space-y-6">
      {store && (
        <Card className="border-emerald-100 bg-emerald-50/30 shadow-none">
          <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1.5">
                <Globe className="w-4 h-4 text-emerald-600" />
                <a
                  href={`${BASE}/shop/${form.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm font-mono text-emerald-800 underline underline-offset-2 truncate hover:text-emerald-950"
                  title="Open your storefront"
                >
                  {storeUrl}
                </a>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={store.isPublished ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}>
                  {store.isPublished ? "Live" : "Draft"}
                </Badge>
                {!store.isPublished && <span className="text-xs text-muted-foreground">Publish to allow customers to order</span>}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <Button size="sm" variant="outline" className="h-9 gap-1.5" onClick={copyUrl} data-testid="button-copy-link">
                {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                {copied ? "Copied" : "Copy Link"}
              </Button>
              <a href={`${BASE}/shop/${form.slug}`} target="_blank" rel="noreferrer">
                <Button size="sm" variant="outline" className="h-9 gap-1.5" data-testid="button-preview">
                  <ExternalLink className="w-4 h-4" /> Open Store
                </Button>
              </a>
              <a href={`${BASE}/shop`} target="_blank" rel="noreferrer">
                <Button size="sm" variant="outline" className="h-9 gap-1.5" data-testid="button-marketplace">
                  <Globe className="w-4 h-4" /> Marketplace
                </Button>
              </a>
              <Button
                size="sm"
                className={`h-9 gap-1.5 ${store.isPublished ? "bg-slate-800 hover:bg-slate-900 text-white" : "bg-emerald-600 hover:bg-emerald-700 text-white"}`}
                onClick={() => { void togglePublish(); }}
                disabled={publishing}
                data-testid="button-toggle-publish"
              >
                {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : store.isPublished ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                {store.isPublished ? "Unpublish" : "Go Live"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {store && !store.isPublished && missingImageCount > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">Products need images before customers can see them</p>
            <p className="mt-1">{missingImageCount} visible product{missingImageCount === 1 ? "" : "s"} will stay hidden when this storefront goes live.</p>
          </div>
        </div>
      )}

      <Card className="shadow-none border-border/50">
        <CardHeader>
          <CardTitle className="text-lg">Store Configuration</CardTitle>
          <CardDescription>Details shown on your public storefront.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSave} className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="space-y-2">
                <label className="text-sm font-medium">Store Display Name</label>
                <Input placeholder="TinyTrendy Kids Store" value={form.displayName} onChange={e => setForm(p => ({ ...p, displayName: e.target.value }))} required data-testid="input-store-name" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Store URL Slug</label>
                <div className="flex">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-muted-foreground text-sm whitespace-nowrap">/shop/</span>
                  <Input
                    placeholder="tinytrendy"
                    value={form.slug}
                    onChange={e => setForm(p => ({ ...p, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/-+/g, "-") }))}
                    className="rounded-l-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:border-input"
                    required
                    data-testid="input-store-slug"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Tagline <span className="text-muted-foreground font-normal">(optional)</span></label>
              <Input placeholder="Quality goods, delivered to your door" value={form.tagline} onChange={e => setForm(p => ({ ...p, tagline: e.target.value }))} data-testid="input-store-tagline" />
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              {artworkInput("logo", "Store Logo", form.logoUrl, logoPreview)}
              {artworkInput("banner", "Store Banner", form.bannerUrl, bannerPreview)}
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div className="space-y-2">
                <label className="text-sm font-medium">WhatsApp Number <span className="text-muted-foreground font-normal">(optional)</span></label>
                <Input placeholder="08012345678" value={form.whatsapp} onChange={e => setForm(p => ({ ...p, whatsapp: e.target.value }))} data-testid="input-store-whatsapp" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Instagram Handle <span className="text-muted-foreground font-normal">(optional)</span></label>
                <Input placeholder="@tinytrendykids" value={form.instagram} onChange={e => setForm(p => ({ ...p, instagram: e.target.value }))} data-testid="input-store-instagram" />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Business Address <span className="text-muted-foreground font-normal">(optional)</span></label>
              <Input placeholder="12 Market Road, Lagos Island, Lagos" value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} data-testid="input-store-address" />
            </div>

            <div className="space-y-2">
              <label htmlFor="store-pickup-state" className="text-sm font-medium">Fez Pickup State <span className="text-muted-foreground font-normal">(optional)</span></label>
              <select
                id="store-pickup-state"
                value={form.pickUpState}
                onChange={event => setForm(current => ({ ...current, pickUpState: event.target.value }))}
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                data-testid="select-store-pickup-state"
              >
                <option value="">Select merchant pickup state</option>
                {NIGERIAN_STATES.map(state => <option key={state} value={state}>{state === "FCT" ? "Federal Capital Territory (FCT)" : state}</option>)}
              </select>
              <p className="text-xs text-muted-foreground">Set the state your Fez parcels ship from so customer delivery quotes use the correct origin.</p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Brand Colour</label>
              <div className="flex items-center gap-3">
                <input type="color" value={form.primaryColor} onChange={e => setForm(p => ({ ...p, primaryColor: e.target.value }))} className="h-9 w-14 p-0.5 rounded-md cursor-pointer border border-input bg-background" data-testid="input-store-color" />
                <span className="text-sm font-mono text-muted-foreground">{form.primaryColor}</span>
              </div>
            </div>

            {error && <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-md flex items-start gap-2"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />{error}</div>}

            <div className="pt-2 flex justify-end">
              <Button type="submit" disabled={saving} className="min-w-[120px]" data-testid="button-save-store">
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {store ? "Save Configuration" : "Create Store"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function ProductsTab() {
  const [products, setProducts] = useState<OnlineProduct[]>([]);
  const [posProducts, setPosProducts] = useState<PosProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [inventoryPickerOpen, setInventoryPickerOpen] = useState(false);
  const [editing, setEditing] = useState<OnlineProduct | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);

  const [form, setForm] = useState({ name: "", description: "", price: "", imageUrls: [] as string[], category: "", productId: "", shippingWeightKg: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);

  const fetchProducts = useCallback(async () => {
    try {
      const [opRes, pRes] = await Promise.all([
        fetch(`${BASE}/api/ecommerce/my-products`, { credentials: "include" }),
        fetch(`${BASE}/api/products`, { credentials: "include" })
      ]);
      if (opRes.ok && pRes.ok) {
        setProducts(await opRes.json());
        setPosProducts(await pRes.json());
      }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const posProductMap = useMemo(() => {
    return new Map(posProducts.map(p => [p.id, p]));
  }, [posProducts]);

  const openAdd = () => {
    setEditing(null);
    setInventoryPickerOpen(false);
    imagePreviews.forEach(preview => { if (preview.startsWith("blob:")) URL.revokeObjectURL(preview); });
    setForm({ name: "", description: "", price: "", imageUrls: [], category: "", productId: "", shippingWeightKg: "" });
    setError(""); setImagePreviews([]); setShowModal(true);
  };
  const openEdit = (p: OnlineProduct) => {
    setEditing(p);
    setInventoryPickerOpen(false);
    imagePreviews.forEach(preview => { if (preview.startsWith("blob:")) URL.revokeObjectURL(preview); });
    const images = p.imageUrls?.length ? p.imageUrls : p.imageUrl ? [p.imageUrl] : [];
    setForm({ name: p.name, description: p.description || "", price: p.price, imageUrls: images, category: p.category || "", productId: String(p.productId || ""), shippingWeightKg: p.shippingWeightKg == null ? "" : String(p.shippingWeightKg) });
    setError(""); setImagePreviews(images); setShowModal(true);
  };

  const handlePosProductSelect = (idStr: string) => {
    const id = parseInt(idStr, 10);
    const posP = posProductMap.get(id);
    setForm(prev => ({
      ...prev,
      productId: idStr,
      name: prev.name || posP?.name || "",
      price: prev.price || String(posP?.price || ""),
    }));
  };

  const handleImageUpload = async (files: File[]) => {
    setError("");
    if (form.imageUrls.length + files.length > MAX_PRODUCT_GALLERY_IMAGES) {
      setError(`A product can have up to ${MAX_PRODUCT_GALLERY_IMAGES} images.`);
      return;
    }
    if (!files.length) return;
    setUploadingImage(true);
    try {
      for (const [index, file] of files.entries()) {
        await validateProductImage(file);
        setUploadProgress(0);
        const urlRes = await fetch(`${BASE}/api/storage/uploads/product-image/request-url`, {
          method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: file.name, size: file.size, contentType: file.type }),
        });
        const urlPayload = await urlRes.json().catch(() => ({}));
        if (!urlRes.ok) throw new Error(urlPayload.error || "Could not prepare this upload. Try again.");
        const { uploadURL, objectPath } = urlPayload;
        await uploadImage(uploadURL, file, setUploadProgress);
        const publicUrl = `${BASE}/api/storage${objectPath}`;
        const previewUrl = URL.createObjectURL(file);
        setForm(current => ({ ...current, imageUrls: [...current.imageUrls, publicUrl] }));
        setImagePreviews(current => [...current, previewUrl]);
        setUploadProgress(Math.round(((index + 1) / files.length) * 100));
      }
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : "Image upload failed. Try another file.");
    } finally {
      setUploadingImage(false);
      setUploadProgress(0);
    }
  };

  const removeProductImage = (index: number) => {
    const preview = imagePreviews[index];
    if (preview?.startsWith("blob:")) URL.revokeObjectURL(preview);
    setForm(current => ({ ...current, imageUrls: current.imageUrls.filter((_, imageIndex) => imageIndex !== index) }));
    setImagePreviews(current => current.filter((_, imageIndex) => imageIndex !== index));
  };

  const makeCoverImage = (index: number) => {
    if (index === 0) return;
    setForm(current => ({
      ...current,
      imageUrls: [current.imageUrls[index], ...current.imageUrls.filter((_, imageIndex) => imageIndex !== index)],
    }));
    setImagePreviews(current => [
      current[index],
      ...current.filter((_, imageIndex) => imageIndex !== index),
    ]);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true); setError("");
    if (!form.productId) {
      setError("Please select a canonical POS product");
      setSaving(false);
      return;
    }
    if (form.shippingWeightKg.trim() && (!Number.isFinite(Number(form.shippingWeightKg)) || Number(form.shippingWeightKg) <= 0)) {
      setError("Shipping weight must be a positive number of kilograms, or left blank.");
      setSaving(false);
      return;
    }
    if (!form.imageUrls.length) {
      setError("Add at least one product image before publishing this product.");
      setSaving(false);
      return;
    }
    try {
      const url = editing ? `${BASE}/api/ecommerce/my-products/${editing.id}` : `${BASE}/api/ecommerce/my-products`;
      const payload: Record<string, unknown> = {
        ...form,
        productId: parseInt(form.productId, 10),
        shippingWeightKg: form.shippingWeightKg.trim() ? Number(form.shippingWeightKg) : null,
      };
      const res = await fetch(url, {
        method: editing ? "PATCH" : "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "Failed to save"); return; }
      setShowModal(false); fetchProducts();
    } catch { setError("Network error"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Remove this product from your online store?")) return;
    setDeleting(id);
    try {
      await fetch(`${BASE}/api/ecommerce/my-products/${id}`, { method: "DELETE", credentials: "include" });
      fetchProducts();
    } finally { setDeleting(null); }
  };

  const toggleAvailable = async (p: OnlineProduct) => {
    if (!p.productId) return;
    if (!p.imageVerified) {
      openEdit(p);
      setError("Add an image before making this product visible.");
      return;
    }
    const res = await fetch(`${BASE}/api/ecommerce/my-products/${p.id}`, {
      method: "PATCH", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ productId: p.productId, isAvailable: !p.isAvailable }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      setError(data.error || "Could not update product visibility.");
    }
    fetchProducts();
  };

  if (loading) return <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          {products.filter(product => product.isAvailable && product.imageVerified).length} visible · {products.filter(product => !product.imageVerified).length} need image
        </p>
        <Button onClick={openAdd} data-testid="button-add-product" className="shrink-0"><Plus className="w-4 h-4 mr-2" /> Publish Product</Button>
      </div>

      {products.length === 0 ? (
        <Card className="border-dashed shadow-none bg-muted/10">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <Package className="w-12 h-12 text-slate-300 mb-4" />
            <p className="font-medium text-slate-900 mb-1">No products published</p>
            <p className="text-sm text-slate-500 max-w-sm mb-6">Select products from your canonical POS inventory to make them available on your online storefront.</p>
            <Button variant="outline" onClick={openAdd} data-testid="button-add-first-product"><Plus className="w-4 h-4 mr-2" /> Publish First Product</Button>
          </CardContent>
        </Card>
      ) : (
        <>
        <div className="hidden overflow-x-auto rounded-md border bg-card md:block">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50 hover:bg-muted/50">
                <TableHead>Product</TableHead>
                <TableHead>Linked POS Product</TableHead>
                <TableHead className="text-right">Price</TableHead>
                <TableHead className="text-right">Available Stock</TableHead>
                <TableHead className="text-center">Visibility</TableHead>
                <TableHead className="w-[80px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {products.map(p => {
                const posP = p.productId == null ? undefined : posProductMap.get(p.productId);
                const stock = posP?.isService ? "∞" : Math.max(0, Math.floor(posP?.stock ?? 0));
                const outOfStock = !posP?.isService && stock === 0;

                return (
                  <TableRow key={p.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {(p.imageUrls?.[0] || p.imageUrl) ? (
                          <img src={p.imageUrls?.[0] || p.imageUrl || ""} alt={p.name} className="w-14 aspect-[4/3] rounded border object-cover bg-muted" />
                        ) : (
                          <div className="w-10 h-10 rounded border bg-muted flex items-center justify-center shrink-0">
                            <Package className="w-4 h-4 text-muted-foreground" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{p.name}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <p className="text-xs text-muted-foreground truncate">{p.category || "No category"}</p>
                            {!p.imageVerified && <Badge variant="outline" className="h-5 px-1.5 text-[10px] border-amber-300 bg-amber-50 text-amber-800">Needs image</Badge>}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {posP ? (
                        <span className="text-xs font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded inline-flex truncate max-w-[150px]">{posP.name}</span>
                      ) : (
                        <span className="text-xs font-medium text-red-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Unlinked</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">{formatCurrency(Number(p.price))}</TableCell>
                    <TableCell className="text-right">
                      {outOfStock ? (
                        <span className="text-red-600 font-medium text-sm">Out of stock</span>
                      ) : (
                        <span className="font-mono text-sm">{stock}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleAvailable(p)}
                        className={`h-7 px-2 text-xs font-medium rounded-full ${!p.imageVerified ? "bg-amber-50 text-amber-800 hover:bg-amber-100" : p.isAvailable ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 hover:text-emerald-800" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
                        data-testid={`toggle-availability-${p.id}`}
                      >
                        {!p.imageVerified ? "Needs image" : p.isAvailable ? "Visible" : "Hidden"}
                      </Button>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500 hover:text-slate-900" onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500 hover:text-red-600 hover:bg-red-50" onClick={() => handleDelete(p.id)} disabled={deleting === p.id} data-testid={`delete-product-${p.id}`}>
                          {deleting === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        <div className="space-y-3 md:hidden">
          {products.map(product => (
            <Card key={product.id} className="shadow-none">
              <CardContent className="flex gap-3 p-3">
                {(product.imageUrls?.[0] || product.imageUrl) ? <img src={product.imageUrls?.[0] || product.imageUrl || ""} alt="" className="h-20 w-20 shrink-0 rounded-md border object-cover" /> : <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-md border bg-muted"><Package className="h-5 w-5 text-muted-foreground" /></div>}
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{product.name}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{product.category || "No category"} · {formatCurrency(Number(product.price))}</p>
                  {!product.imageVerified && <Badge variant="outline" className="mt-1 border-amber-300 bg-amber-50 text-[10px] text-amber-800">Needs image</Badge>}
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <Button type="button" size="sm" variant="outline" className="h-8" onClick={() => toggleAvailable(product)}>{product.isAvailable ? "Visible" : "Hidden"}</Button>
                    <Button type="button" size="icon" variant="ghost" aria-label={`Edit ${product.name}`} className="h-8 w-8" onClick={() => openEdit(product)}><Pencil className="h-4 w-4" /></Button>
                    <Button type="button" size="icon" variant="ghost" aria-label={`Remove ${product.name}`} className="h-8 w-8 text-red-600" onClick={() => handleDelete(product.id)} disabled={deleting === product.id}>
                      {deleting === product.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        </>
      )}

      <Dialog open={showModal} onOpenChange={(open) => {
        setShowModal(open);
        if (!open) setInventoryPickerOpen(false);
      }}>
        <DialogContent className="sm:max-w-[560px] p-0 overflow-hidden flex flex-col max-h-[90vh]">
          <div className="p-6 pb-4 border-b bg-muted/10 shrink-0">
            <DialogTitle>{editing ? "Edit Online Product" : "Publish to Online Store"}</DialogTitle>
            <DialogDescription className="mt-1.5">Link a canonical inventory item and customize how it appears online.</DialogDescription>
          </div>
          <form id="ecommerce-product-form" onSubmit={handleSave} className="p-6 space-y-5 overflow-y-auto flex-1 min-h-0">
            <div className="space-y-2">
              <label className="text-sm font-medium">Canonical POS Product <span className="text-red-500">*</span></label>
              <Popover open={inventoryPickerOpen} onOpenChange={setInventoryPickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={inventoryPickerOpen}
                    disabled={!!editing}
                    className="h-10 w-full justify-between font-normal"
                    data-testid="button-select-inventory-product"
                  >
                    <span className="truncate">
                      {form.productId
                        ? posProductMap.get(Number(form.productId))?.name ?? "Selected inventory product"
                        : "Select a product from inventory..."}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-[--radix-popover-trigger-width] p-0">
                  <Command>
                    <CommandInput
                      placeholder="Search inventory products..."
                      className="h-9"
                      data-testid="input-search-inventory-products"
                    />
                    <CommandList className="max-h-60 overflow-y-auto">
                      <CommandEmpty>No matching inventory products.</CommandEmpty>
                      <CommandGroup>
                        {posProducts.map(product => (
                          <CommandItem
                            key={product.id}
                            value={`${product.name} ${product.id}`}
                            onSelect={() => {
                              handlePosProductSelect(String(product.id));
                              setInventoryPickerOpen(false);
                            }}
                            data-testid={`option-inventory-product-${product.id}`}
                          >
                            <Check className={`mr-2 h-4 w-4 ${form.productId === String(product.id) ? "opacity-100" : "opacity-0"}`} />
                            <span className="min-w-0 flex-1 truncate">{product.name}</span>
                            <span className="ml-3 shrink-0 text-xs text-muted-foreground">
                              {product.isService ? "Service" : `${Math.floor(product.stock)} in stock`}
                            </span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <p className="text-xs text-muted-foreground">Inventory availability syncs automatically with this product.</p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Online Display Name <span className="text-red-500">*</span></label>
              <Input placeholder="Enter product name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required data-testid="input-product-name" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Selling Price (₦) <span className="text-red-500">*</span></label>
                <Input type="number" min="0" step="0.01" placeholder="0.00" value={form.price} onChange={e => setForm(p => ({ ...p, price: e.target.value }))} required data-testid="input-product-price" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Category</label>
                <Input placeholder="e.g. Dresses" value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} data-testid="input-product-category" />
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="product-shipping-weight" className="text-sm font-medium">Shipping Weight (kg) <span className="text-muted-foreground font-normal">(optional)</span></label>
              <Input
                id="product-shipping-weight"
                type="number"
                min="0.01"
                step="0.01"
                placeholder="e.g. 0.5"
                value={form.shippingWeightKg}
                onChange={event => setForm(current => ({ ...current, shippingWeightKg: event.target.value }))}
                data-testid="input-product-shipping-weight"
              />
              <p className="text-xs text-muted-foreground">A positive weight is required for this product to use live Fez delivery quotes.</p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Description</label>
              <Textarea
                placeholder="Product details, material, care instructions..."
                value={form.description}
                onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                className="resize-none h-20"
                data-testid="input-product-description"
              />
            </div>

            <div className="space-y-2">
              <div>
                <label className="text-sm font-medium">Product Photos <span className="text-red-500">*</span></label>
                <p className="text-xs text-muted-foreground mt-1">Add up to {MAX_PRODUCT_GALLERY_IMAGES} photos. The first photo is the storefront cover. Minimum: 500×500px. Recommended: 1200×900px (4:3); accepted photos are optimized to 1200×900 WebP. JPG/PNG/WebP, up to 5MB each.</p>
              </div>
              <div className="p-3 border rounded-lg bg-muted/20 space-y-3">
                {imagePreviews.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {imagePreviews.map((preview, index) => (
                      <div key={`${preview}-${index}`} className="min-w-0 rounded-md border bg-background overflow-hidden">
                        <div className="aspect-square relative">
                          <img src={preview} alt={`Product photo ${index + 1}`} className="w-full h-full object-cover" />
                          {index === 0 && <Badge className="absolute left-2 top-2 text-[10px]">Cover</Badge>}
                          <button
                            type="button"
                            aria-label={`Remove product photo ${index + 1}`}
                            data-testid={`button-remove-product-image-${index}`}
                            onClick={() => removeProductImage(index)}
                            className="absolute right-2 top-2 h-7 w-7 rounded-full bg-white/95 text-red-600 shadow flex items-center justify-center hover:bg-white"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        {index > 0 && (
                          <button
                            type="button"
                            data-testid={`button-make-cover-${index}`}
                            onClick={() => makeCoverImage(index)}
                            className="w-full py-1.5 text-[11px] font-medium text-muted-foreground hover:text-foreground"
                          >
                            Make cover
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="aspect-[4/3] max-h-44 rounded-md border border-dashed bg-background flex flex-col items-center justify-center gap-2 text-muted-foreground">
                    <Package className="w-8 h-8" />
                    <span className="text-xs">No photos added</span>
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  <label className={`inline-flex items-center justify-center gap-2 h-9 px-4 border rounded-md text-sm font-medium cursor-pointer transition-colors bg-background hover:bg-muted ${uploadingImage || imagePreviews.length >= MAX_PRODUCT_GALLERY_IMAGES ? "opacity-50 pointer-events-none" : ""}`}>
                    {uploadingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                    {uploadingImage ? "Uploading..." : `Add photos (${imagePreviews.length}/${MAX_PRODUCT_GALLERY_IMAGES})`}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      multiple
                      className="hidden"
                      aria-label="Add product photos"
                      data-testid="input-product-images"
                      onChange={e => { const files = Array.from(e.target.files ?? []); if (files.length) void handleImageUpload(files); e.target.value = ""; }}
                      disabled={uploadingImage || imagePreviews.length >= MAX_PRODUCT_GALLERY_IMAGES}
                    />
                  </label>
                </div>
                {uploadingImage && (
                  <div className="space-y-1">
                    <div className="h-2 rounded-full bg-slate-200 overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${uploadProgress}%` }} /></div>
                    <p className="text-xs text-muted-foreground">Uploading… {uploadProgress}%</p>
                  </div>
                )}
                {editing && !editing.imageVerified && <p className="text-xs text-amber-700">This product’s existing photos are not verified for the storefront. Add a verified photo before customers can see it.</p>}
                {editing && editing.imageVerified && !form.imageUrls.length && <p className="text-xs text-amber-700">Saving without photos will hide this product from customers until a new photo is added.</p>}
              </div>
            </div>

            {error && <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-md flex items-start gap-2"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />{error}</div>}

          </form>
          <div className="p-6 pt-4 border-t bg-background flex justify-end gap-2 shrink-0">
            <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="submit" form="ecommerce-product-form" disabled={saving || uploadingImage} data-testid="button-save-product">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {editing ? "Save Changes" : "Publish Product"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OrdersTab() {
  const [orders, setOrders] = useState<OnlineOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [orderSummary, setOrderSummary] = useState({ pendingOrders: 0, paidRevenueKobo: 0, reviewOrders: 0 });
  const [selectedOrder, setSelectedOrder] = useState<OnlineOrder | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundQuantities, setRefundQuantities] = useState<Record<number, number>>({});
  const [refundSource, setRefundSource] = useState<"paystack" | "manual">("paystack");
  const [providerRefundId, setProviderRefundId] = useState("");
  const [restoreInventory, setRestoreInventory] = useState(true);
  const [refundReason, setRefundReason] = useState("");
  const [refundError, setRefundError] = useState("");
  const [refunding, setRefunding] = useState(false);
  const [refundRequestKey, setRefundRequestKey] = useState("");

  // Assignment states
  const [assigningOrder, setAssigningOrder] = useState<OnlineOrder | null>(null);
  const [riders, setRiders] = useState<any[]>([]);
  const [selectedRiderId, setSelectedRiderId] = useState<string>("");
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [assignmentResult, setAssignmentResult] = useState<{ pickupCode: string, deliveryCode: string, rider: any } | null>(null);
  const [assignmentError, setAssignmentError] = useState("");

  const [pickupCode, setPickupCode] = useState("");
  const [deliveryCode, setDeliveryCode] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [proofText, setProofText] = useState("");
  const [assignmentSubmitting, setAssignmentSubmitting] = useState<"pickup" | "delivery" | null>(null);
  const [assignmentSubmitError, setAssignmentSubmitError] = useState("");
  const [fezBookingFormOpen, setFezBookingFormOpen] = useState(false);
  const [fezPickUpAddress, setFezPickUpAddress] = useState("");
  const [fezBookingSubmitting, setFezBookingSubmitting] = useState(false);
  const [fezBookingError, setFezBookingError] = useState("");
  const [fezLookupLoading, setFezLookupLoading] = useState(false);
  const [fezProgress, setFezProgress] = useState<{ status: string; history: Array<{ status: string; description: string; at: string | null }> } | null>(null);
  const [fezProgressError, setFezProgressError] = useState("");
  const [fezBookingUnknownOrderIds, setFezBookingUnknownOrderIds] = useState<Set<number>>(() => new Set());

  const handlePickup = async (assignmentId: number) => {
    if (!pickupCode.trim()) return;
    setAssignmentSubmitting("pickup"); setAssignmentSubmitError("");
    try {
      const res = await fetch(`${BASE}/api/ecommerce/assignments/${assignmentId}/pickup`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: pickupCode })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to record pickup");
      setPickupCode("");
      await fetchOrders();
    } catch (err: any) {
      setAssignmentSubmitError(err.message);
    } finally {
      setAssignmentSubmitting(null);
    }
  };

  const handleDelivery = async (assignmentId: number) => {
    if (!deliveryCode.trim() || !recipientName.trim() || !proofText.trim()) return;
    setAssignmentSubmitting("delivery"); setAssignmentSubmitError("");
    try {
      const res = await fetch(`${BASE}/api/ecommerce/assignments/${assignmentId}/deliver`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: deliveryCode, recipientName, proofText })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to record delivery");
      setDeliveryCode(""); setRecipientName(""); setProofText("");
      await fetchOrders();
    } catch (err: any) {
      setAssignmentSubmitError(err.message);
    } finally {
      setAssignmentSubmitting(null);
    }
  };

  const fetchOrders = useCallback(async () => {
    try {
      const res = await fetch(`${BASE}/api/ecommerce/my-orders?page=${page}&pageSize=20`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setOrders(data.orders);
        setTotalOrders(data.total);
        setTotalPages(data.totalPages);
        setOrderSummary(data.summary);
        setFezBookingUnknownOrderIds(current => {
          const reconciled = new Set(current);
          for (const order of data.orders as OnlineOrder[]) {
            if (Object.prototype.hasOwnProperty.call(order, "fezBooking")) reconciled.delete(order.id);
          }
          return reconciled;
        });
        if (selectedOrder) {
          const updatedSelected = data.orders.find((o: OnlineOrder) => o.id === selectedOrder.id);
          if (updatedSelected) setSelectedOrder(updatedSelected);
        }
      }
    } finally { setLoading(false); }
  }, [selectedOrder, page]);

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 10000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  useEffect(() => {
    setFezBookingFormOpen(false);
    setFezBookingError("");
    setFezPickUpAddress("");
    setFezProgress(null);
    setFezProgressError("");
  }, [selectedOrder?.id]);

  useEffect(() => {
    const orderId = selectedOrder?.id;
    if (!orderId || selectedOrder?.fezBooking?.status !== "success") return;
    let active = true;
    const refresh = async () => {
      try {
        const response = await fetch(`${BASE}/api/ecommerce/my-orders/${orderId}/fez-tracking`, { credentials: "include" });
        if (!response.ok) throw new Error("Live Fez tracking is temporarily unavailable.");
        const progress = await response.json();
        if (active) { setFezProgress(progress); setFezProgressError(""); }
      } catch {
        if (active) setFezProgressError("Live Fez tracking is temporarily unavailable. The last known status is shown below.");
      }
    };
    void refresh();
    const timer = setInterval(refresh, 60_000);
    return () => { active = false; clearInterval(timer); };
  }, [selectedOrder?.id, selectedOrder?.fezBooking?.status]);

  const lookupFezBooking = async () => {
    if (!selectedOrder) return;
    const orderId = selectedOrder.id;
    setFezLookupLoading(true);
    setFezBookingError("");
    try {
      const response = await fetch(`${BASE}/api/ecommerce/my-orders/${orderId}/fez-booking/lookup`, {
        method: "POST", credentials: "include",
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Fez lookup failed. Do not rebook or dispatch a rider.");
      if (data.status === "success") {
        updateFezBooking(orderId, { status: "success", orderNo: data.orderNo, providerStatus: data.providerStatus, waybillNumber: data.waybillNumber });
        setFezBookingUnknownOrderIds(current => { const next = new Set(current); next.delete(orderId); return next; });
        await fetchOrders();
      } else {
        setFezBookingError(data.error || "The booking is still unresolved. Do not rebook or dispatch a rider.");
      }
    } catch (error) {
      setFezBookingError(error instanceof Error ? error.message : "Fez lookup failed. Keep dispatch paused.");
    } finally {
      setFezLookupLoading(false);
    }
  };

  const updateFezBooking = (orderId: number, fezBooking: OnlineOrder["fezBooking"]) => {
    setOrders(current => current.map(order => order.id === orderId ? { ...order, fezBooking } : order));
    setSelectedOrder(current => current?.id === orderId ? { ...current, fezBooking } : current);
  };

  const submitFezBooking = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedOrder) return;
    const recipientState = selectedOrder.fezBooking?.recipientState ?? selectedOrder.recipientState;
    const weightKg = selectedOrder.fezBooking?.weightKg ?? selectedOrder.weightKg ?? selectedOrder.shippingWeightKg;
    const confirmed = window.confirm(
      `Book Fez pickup for order ${selectedOrder.orderNumber}${recipientState ? ` to ${recipientState}` : ""}${weightKg ? ` (${weightKg} kg)` : ""}? This uses the delivery snapshot recorded at checkout.`,
    );
    if (!confirmed) return;

    setFezBookingSubmitting(true);
    setFezBookingError("");
    try {
      const res = await fetch(`${BASE}/api/ecommerce/my-orders/${selectedOrder.id}/fez-booking`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(fezPickUpAddress.trim() ? { pickUpAddress: fezPickUpAddress.trim() } : {}),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status >= 500) {
        setFezBookingUnknownOrderIds(current => new Set(current).add(selectedOrder.id));
        setFezBookingError("Fez booking outcome is unknown. Reconcile the booking before dispatching a manual rider; do not submit another booking yet.");
        return;
      }
      if (!res.ok) {
        setFezBookingError(data.error || "Fez pickup booking could not be created.");
        return;
      }
      if (typeof data.status !== "string" || !data.status.trim()) {
        setFezBookingUnknownOrderIds(current => new Set(current).add(selectedOrder.id));
        setFezBookingError("Fez returned an unrecognized booking response. Reconcile the booking before dispatching a manual rider or trying again.");
        return;
      }

      updateFezBooking(selectedOrder.id, {
        status: data.status,
        orderNo: data.orderNo ?? null,
        waybillNumber: data.waybillNumber,
        error: data.error ?? null,
      });
      setFezBookingUnknownOrderIds(current => {
        const updated = new Set(current);
        updated.delete(selectedOrder.id);
        return updated;
      });
      setFezBookingFormOpen(false);
      await fetchOrders();
    } catch (error) {
      setFezBookingUnknownOrderIds(current => new Set(current).add(selectedOrder.id));
      console.error("Fez booking request outcome is unknown", error);
      setFezBookingError("Fez booking outcome is unknown. Reconcile the booking before dispatching a manual rider; do not submit another booking yet.");
    } finally {
      setFezBookingSubmitting(false);
    }
  };

  const updateStatus = async (orderId: number, status: string) => {
    setUpdatingStatus(status);
    try {
      const res = await fetch(`${BASE}/api/ecommerce/my-orders/${orderId}`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status }),
      });
      if (res.ok) {
        await fetchOrders();
      }
    } finally { setUpdatingStatus(null); }
  };

  const openAssignModal = async (order: OnlineOrder) => {
    const bookingStatus = order.fezBooking?.status?.toLowerCase() ?? "";
    const bookingFailed = ["failed", "error", "rejected", "cancelled"].includes(bookingStatus);
    const bookingNeedsReconciliation = ["unknown", "pending"].includes(bookingStatus);
    if (order.deliveryProvider === "fez" && (fezBookingUnknownOrderIds.has(order.id) || bookingNeedsReconciliation || (!!order.fezBooking && !bookingFailed))) {
      setFezBookingError(fezBookingUnknownOrderIds.has(order.id) || bookingNeedsReconciliation
        ? "Reconcile the Fez booking before dispatching a manual rider."
        : "A Fez booking already exists. Do not dispatch a second rider.");
      return;
    }
    setAssigningOrder(order);
    setAssignmentResult(null);
    setSelectedRiderId("");
    setAssignmentError("");
    try {
      const res = await fetch(`${BASE}/api/ecommerce/riders`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setRiders(data.filter((r: any) => r.active));
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleAssign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedRiderId || !assigningOrder) return;
    setAssignmentLoading(true); setAssignmentError("");
    try {
      const res = await fetch(`${BASE}/api/ecommerce/my-orders/${assigningOrder.id}/assignment`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ riderId: Number(selectedRiderId) })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to assign rider");

      setAssignmentResult({ pickupCode: data.pickupCode, deliveryCode: data.deliveryCode, rider: data.rider });
          } catch (err: any) {
      setAssignmentError(err.message);
    } finally {
      setAssignmentLoading(false);
    }
  };

  const handleWhatsApp = async (assignmentId: number) => {
    try {
      const res = await fetch(`${BASE}/api/ecommerce/assignments/${assignmentId}/whatsapp`, {
        method: "POST", credentials: "include"
      });
      const data = await res.json();
      if (res.ok && data.url) {
        window.open(data.url, "_blank");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const refundedQuantity = (order: OnlineOrder, itemId: number) =>
    (order.refunds ?? []).filter(refund => ["pending", "processed"].includes(refund.status))
      .flatMap(refund => refund.items ?? []).filter(item => item.orderItemId === itemId)
      .reduce((sum, item) => sum + item.quantity, 0);

  const openRefund = (order: OnlineOrder) => {
    setRefundQuantities(Object.fromEntries(order.items.map(item => [item.id, Math.max(0, item.quantity - refundedQuantity(order, item.id))])));
    setRefundSource("paystack"); setProviderRefundId(""); setRestoreInventory(true); setRefundReason(""); setRefundError(""); setRefundOpen(true);
    setRefundRequestKey(crypto.randomUUID());
  };

  const submitRefund = async () => {
    if (!selectedOrder) return;
    const items = selectedOrder.items.map(item => ({ orderItemId: item.id, quantity: refundQuantities[item.id] ?? 0 })).filter(item => item.quantity > 0);
    if (!items.length) { setRefundError("Choose at least one item quantity."); return; }
    setRefunding(true); setRefundError("");
    try {
      const res = await fetch(`${BASE}/api/ecommerce/my-orders/${selectedOrder.id}/refunds`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json", "Idempotency-Key": refundRequestKey },
        body: JSON.stringify({ items, source: refundSource, providerRefundId: refundSource === "manual" ? providerRefundId : undefined, restoreInventory, reason: refundReason }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) { setRefundError(body.error || "Refund could not be created."); return; }
      setRefundOpen(false);
      await fetchOrders();
    } catch {
      setRefundError("Network error. Check the refund in Paystack before trying again.");
    } finally { setRefunding(false); }
  };

  const pendingCount = orderSummary.pendingOrders;
  const totalRevenue = orderSummary.paidRevenueKobo / 100;
  const reviewCount = orderSummary.reviewOrders;
  const selectedIsFezDelivery = selectedOrder?.deliveryProvider === "fez" && selectedOrder.fulfillmentType !== "pickup";
  const selectedFezBooking = selectedOrder?.fezBooking ?? null;
  const selectedFezBookingStatus = selectedFezBooking?.status?.toLowerCase() ?? "";
  const selectedFezBookingFailed = ["failed", "error", "rejected", "cancelled"].includes(selectedFezBookingStatus);
  const selectedFezBookingActive = !!selectedFezBooking && !selectedFezBookingFailed;
  const selectedFezBookingUnknown = !!selectedOrder && (
    fezBookingUnknownOrderIds.has(selectedOrder.id) || ["unknown", "pending"].includes(selectedFezBookingStatus)
  );
  const selectedFezRecipientState = selectedFezBooking?.recipientState ?? selectedOrder?.recipientState ?? null;
  const selectedFezWeightKg = selectedFezBooking?.weightKg ?? selectedOrder?.weightKg ?? selectedOrder?.shippingWeightKg ?? null;
  const selectedFezFeeKobo = selectedOrder?.quotedFeeKobo ?? selectedOrder?.deliveryFeeKobo ?? null;

  if (loading && orders.length === 0) return <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="flex flex-col lg:flex-row lg:h-[calc(100vh-220px)] min-h-[500px] gap-4 lg:gap-6">
      <div className={`flex-1 flex flex-col min-w-0 ${selectedOrder ? "hidden lg:flex" : "flex"}`}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6 shrink-0">
          <Card className="shadow-none border-border/50 bg-muted/10">
            <CardContent className="p-4">
              <div className="text-sm font-medium text-muted-foreground mb-1">Total Orders</div>
              <div className="text-2xl font-semibold tracking-tight">{totalOrders}</div>
            </CardContent>
          </Card>
          <Card className="shadow-none border-amber-100 bg-amber-50/50">
            <CardContent className="p-4">
              <div className="text-sm font-medium text-amber-700 mb-1">Needs Fulfillment</div>
              <div className="text-2xl font-semibold tracking-tight text-amber-900">{pendingCount}</div>
            </CardContent>
          </Card>
          <Card className="shadow-none border-emerald-100 bg-emerald-50/50">
            <CardContent className="p-4">
              <div className="text-sm font-medium text-emerald-700 mb-1">Paid Revenue</div>
              <div className="text-2xl font-semibold tracking-tight text-emerald-900">{formatCurrency(totalRevenue)}</div>
            </CardContent>
          </Card>
        </div>

        {reviewCount > 0 && (
          <div className="mb-4 p-3 bg-red-50 border border-red-100 text-red-800 text-sm rounded-md flex items-center gap-3 shrink-0">
            <AlertCircle className="w-5 h-5 text-red-600 shrink-0" />
            <p>You have <strong>{reviewCount}</strong> order{reviewCount > 1 ? 's' : ''} under manual payment review. Please check your payment provider dashboard.</p>
          </div>
        )}

        <div className="border rounded-md bg-card flex-1 overflow-auto">
          {orders.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center p-8">
              <ShoppingBag className="w-12 h-12 text-slate-300 mb-4" />
              <p className="font-medium text-slate-900 mb-1">No orders yet</p>
              <p className="text-sm text-slate-500 max-w-sm">Share your store link to start receiving orders.</p>
            </div>
          ) : (
            <>
            <div className="space-y-2 p-3 md:hidden">
              {orders.map(order => (
                <button key={order.id} type="button" onClick={() => setSelectedOrder(order)} className={`w-full rounded-lg border p-3 text-left transition-colors ${selectedOrder?.id === order.id ? "border-primary bg-primary/5" : "bg-card hover:bg-muted/40"}`} aria-label={`View order ${order.orderNumber} for ${order.customerName}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-mono text-sm font-semibold">{order.orderNumber}</p>
                      <p className="mt-1 truncate text-sm font-medium">{order.customerName}</p>
                      <p className="truncate text-xs text-muted-foreground">{order.customerEmail}</p>
                    </div>
                    <p className="shrink-0 text-sm font-semibold">{formatCurrency(getOrderTotal(order))}</p>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-1.5">
                      {getStatusBadge(order.status)}
                      {order.deliveryProvider === "fez" && <Badge variant="outline" className="text-xs">Fez{order.fezBooking?.status ? ` · ${order.fezBooking.status}` : ""}</Badge>}
                    </div>
                    <span className="text-xs text-muted-foreground">{format(new Date(order.createdAt), "MMM d, HH:mm")}</span>
                  </div>
                </button>
              ))}
            </div>
            <div className="hidden overflow-x-auto md:block">
            <Table>
              <TableHeader className="sticky top-0 bg-muted/80 backdrop-blur z-10">
                <TableRow>
                  <TableHead className="w-[100px]">Order</TableHead>
                  <TableHead>Customer</TableHead>
                   <TableHead className="text-right">Total</TableHead>
                   <TableHead className="text-right">Seller net</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead>Fulfillment</TableHead>
                  <TableHead className="text-right">Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map(o => (
                  <TableRow
                    key={o.id}
                    className={`cursor-pointer transition-colors ${selectedOrder?.id === o.id ? "bg-muted/80 hover:bg-muted/80" : "hover:bg-muted/50"}`}
                    onClick={() => setSelectedOrder(o)}
                    data-testid={`row-order-${o.id}`}
                  >
                    <TableCell className="font-mono text-sm font-medium text-slate-700">{o.orderNumber}</TableCell>
                    <TableCell>
                      <div className="min-w-0 max-w-[200px]">
                        <p className="font-medium text-sm truncate">{o.customerName}</p>
                        <p className="text-xs text-muted-foreground truncate">{o.customerEmail}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">{formatCurrency(getOrderTotal(o))}</TableCell>
                    <TableCell className="text-right font-mono text-sm text-emerald-700">{o.sellerNetKobo == null ? "—" : formatCurrency(o.sellerNetKobo / 100)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <div className={`w-2 h-2 rounded-full ${o.paymentStatus === 'paid' ? 'bg-emerald-500' : o.paymentStatus === 'failed' ? 'bg-red-500' : 'bg-amber-400'}`} />
                        <span className="text-xs font-medium capitalize text-slate-700">{o.paymentStatus}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-1.5">
                        {getStatusBadge(o.status)}
                        {o.deliveryProvider === "fez" && <Badge variant="outline" className="text-xs">Fez{o.fezBooking?.status ? ` · ${o.fezBooking.status}` : ""}</Badge>}
                      </div>
                    </TableCell>
                    <TableCell className="text-right text-xs font-medium text-slate-500 whitespace-nowrap">
                      {format(new Date(o.createdAt), "MMM d, HH:mm")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
            </>
          )}
        </div>
        {totalPages > 1 && (
          <nav aria-label="Merchant order pages" className="mt-3 flex items-center justify-center gap-3 shrink-0">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(current => Math.max(1, current - 1))}>Previous</Button>
            <span className="text-sm text-muted-foreground" aria-live="polite">Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(current => Math.min(totalPages, current + 1))}>Next</Button>
          </nav>
        )}
      </div>

      {/* Order Details Side Panel */}
      {selectedOrder && (
        <Card className="w-full lg:w-[400px] xl:w-[450px] shrink-0 shadow-xl lg:shadow-none lg:border-l lg:border-l-border flex flex-col rounded-none sm:rounded-md h-full overflow-hidden">
          <div className="p-4 border-b bg-card flex items-center justify-between shrink-0">
            <div>
              <h3 className="font-semibold text-lg font-mono tracking-tight">{selectedOrder.orderNumber}</h3>
              <p className="text-xs text-muted-foreground font-medium">{format(new Date(selectedOrder.createdAt), "MMMM do yyyy, h:mm a")}</p>
            </div>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500 lg:hidden" onClick={() => setSelectedOrder(null)}>
              <X className="w-4 h-4" />
            </Button>
          </div>

          <div className="p-0 flex-1 overflow-y-auto bg-slate-50/50">
            {selectedOrder.status === "payment_review" && (
              <div className="m-4 p-3 bg-amber-50 border border-amber-200 text-amber-900 text-sm rounded-md flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold mb-1 tracking-tight">Manual Review Required</p>
                  <p className="text-amber-800/80 text-xs font-medium">Payment provider flagged this transaction. Verify in your provider dashboard before fulfilling.</p>
                </div>
              </div>
            )}

            <div className="px-4 py-5 space-y-6">
              <div className="flex gap-3 items-center bg-card p-3 rounded-md border shadow-sm">
                <div className="flex-1">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-widest font-semibold mb-1.5">Fulfillment</p>
                  {getStatusBadge(selectedOrder.status)}
                </div>
                <div className="w-px h-10 bg-border" />
                <div className="flex-1 pl-2">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-widest font-semibold mb-1.5">Payment</p>
                  <Badge variant="outline" className={`capitalize font-medium ${selectedOrder.paymentStatus === 'paid' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-700'}`}>
                    {selectedOrder.paymentStatus}
                  </Badge>
                </div>
              </div>

              {selectedOrder.paymentStatus === "paid" && (() => {
                const payout = selectedOrder.payout ?? { status: "pending" as const, providerStatus: null, expectedAt: null, settledAt: null, updatedAt: null };
                const presentation = payoutPresentation[payout.status] ?? payoutPresentation.review;
                return (
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-widest font-semibold mb-2">Bank payout</p>
                    <div className="text-sm bg-card rounded-md p-3 border shadow-sm space-y-2">
                      <div className="flex items-center justify-between gap-3">
                        <Badge variant="outline" className={presentation.className}>{presentation.label}</Badge>
                        {payout.settledAt && <span className="text-xs text-muted-foreground">{format(new Date(payout.settledAt), "MMM d, yyyy · h:mm a")}</span>}
                      </div>
                      <p className="text-xs leading-relaxed text-slate-600">{presentation.detail}</p>
                      {payout.expectedAt && payout.status === "pending" && (
                        <p className="text-xs font-medium text-amber-800">Expected by {format(new Date(payout.expectedAt), "MMM d, yyyy · h:mm a")}</p>
                      )}
                    </div>
                  </div>
                );
              })()}

              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-widest font-semibold mb-2">Customer Details</p>
                <div className="text-sm bg-card rounded-md p-3 border shadow-sm space-y-1">
                  <p className="font-medium">{selectedOrder.customerName}</p>
                  <p className="text-slate-600 flex items-center gap-1.5"><Send className="w-3.5 h-3.5" /> {selectedOrder.customerEmail}</p>
                  {selectedOrder.customerPhone && <p className="text-slate-600 flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {selectedOrder.customerPhone}</p>}
                </div>
              </div>

              {selectedOrder.fulfillmentType === "pickup" ? (
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-widest font-semibold mb-2">Store Pickup</p>
                  <div className="text-sm bg-card rounded-md p-3 border shadow-sm text-slate-700 leading-relaxed flex items-start gap-2">
                    <MapIcon className="w-4 h-4 shrink-0 mt-0.5 text-primary" />
                    <p>Customer will pick up at store.</p>
                  </div>
                </div>
              ) : (
                selectedOrder.deliveryAddress && (
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-widest font-semibold mb-2">Delivery Details</p>
                    <div className="text-sm bg-card rounded-md p-3 border shadow-sm text-slate-700 leading-relaxed">
                      <div className="flex items-start gap-2 mb-2 pb-2 border-b">
                        <MapIcon className="w-4 h-4 shrink-0 mt-0.5 text-primary" />
                        <p>{selectedOrder.deliveryAddress}</p>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="font-medium">Zone:</span> {selectedOrder.deliveryZoneName || "Custom"}
                      </div>
                    </div>
                  </div>
                )
              )}

              {selectedIsFezDelivery && (selectedOrder.paymentStatus === "paid" || selectedFezBooking) && (
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-widest font-semibold mb-2">Fez pickup booking</p>
                  <div className="text-sm bg-card rounded-md p-3 border shadow-sm space-y-3">
                    <div className="rounded-md bg-slate-50 border p-3 space-y-1 text-xs">
                      <p className="font-semibold text-slate-700">Checkout delivery snapshot</p>
                      <p className="text-slate-600">Destination: {selectedFezRecipientState || "Not provided by the order record"}</p>
                      <p className="text-slate-600">Shipment weight: {selectedFezWeightKg != null ? `${selectedFezWeightKg} kg` : "Not provided by the order record"}</p>
                      {selectedFezFeeKobo != null && <p className="text-slate-600">Quoted delivery fee (incl. VAT): {formatCurrency(selectedFezFeeKobo / 100)}</p>}
                    </div>
                    {selectedFezBooking ? (
                      <div className="space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant="outline" className="capitalize">{selectedFezBooking.status}</Badge>
                          {selectedFezBooking.orderNo && <span className="font-mono text-xs text-slate-700">Reference: {selectedFezBooking.orderNo}</span>}
                          {selectedFezBooking.waybillNumber && <span className="font-mono text-xs text-slate-700">Client waybill: {selectedFezBooking.waybillNumber}</span>}
                        </div>
                        {selectedFezBooking.status === "success" && (
                          <div className="space-y-1 text-xs">
                            <p>Fez delivery: {fezProgress?.status ?? selectedFezBooking.providerStatus ?? "Awaiting status update"}</p>
                            {fezProgress?.history.map((event, index) => <p key={`${event.at}-${index}`} className="text-muted-foreground">{event.status}{event.at ? ` · ${event.at}` : ""}{event.description ? ` — ${event.description}` : ""}</p>)}
                            {fezProgressError && <p className="text-amber-700">{fezProgressError}</p>}
                          </div>
                        )}
                        {selectedFezBooking.error && <p className="text-xs text-red-700">{selectedFezBooking.error}</p>}
                        {selectedFezBookingActive && <p className="text-xs text-muted-foreground">A Fez booking exists; use its status and reference to coordinate this delivery instead of assigning a second rider.</p>}
                        {selectedFezBookingFailed && <p className="text-xs text-muted-foreground">Use manual rider dispatch as a fallback for this failed Fez booking.</p>}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">No Fez pickup has been booked. Booking is a separate merchant action and will not happen automatically.</p>
                    )}
                    {selectedFezBookingUnknown && (
                      <div className="rounded border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 space-y-2" role="alert">
                        <p className="font-semibold">Booking outcome needs reconciliation</p>
                        <p>Look up this booking by its client waybill without submitting it again. A not-found result does not clear the dispatch hold.</p>
                        <Button type="button" variant="outline" size="sm" disabled={fezLookupLoading} onClick={() => { void lookupFezBooking(); }}>{fezLookupLoading ? "Checking Fez…" : "Look up Fez booking"}</Button>
                      </div>
                    )}
                    {fezBookingError && !fezBookingFormOpen && <p className="text-xs text-amber-700" role="alert">{fezBookingError}</p>}
                    {!selectedFezBooking && !selectedFezBookingUnknown && selectedOrder.paymentStatus === "paid" && (
                      !fezBookingFormOpen ? (
                        <Button type="button" className="w-full" onClick={() => { setFezBookingError(""); setFezBookingFormOpen(true); }}>
                          Book Fez pickup
                        </Button>
                      ) : (
                        <form className="space-y-3 border-t pt-3" onSubmit={submitFezBooking}>
                          <p className="text-xs text-muted-foreground">Booking uses the destination and shipment weight captured when the customer checked out. You may override the pickup address for this booking.</p>
                          <div className="space-y-1">
                            <label className="text-xs font-medium" htmlFor="fez-pickup-address">Pickup address <span className="text-muted-foreground">(optional)</span></label>
                            <Input id="fez-pickup-address" value={fezPickUpAddress} onChange={event => setFezPickUpAddress(event.target.value)} placeholder="Merchant pickup address" />
                          </div>
                          {fezBookingError && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2" role="alert">{fezBookingError}</div>}
                          <div className="flex flex-col-reverse sm:flex-row gap-2">
                            <Button type="button" variant="outline" className="flex-1" disabled={fezBookingSubmitting} onClick={() => setFezBookingFormOpen(false)}>Cancel</Button>
                            <Button type="submit" className="flex-1" disabled={fezBookingSubmitting}>
                              {fezBookingSubmitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                              Confirm & book pickup
                            </Button>
                          </div>
                        </form>
                      )
                    )}
                  </div>
                </div>
              )}

              {selectedOrder.notes && (
                <div>
                  <p className="text-[10px] text-amber-700 uppercase tracking-widest font-semibold mb-2">Customer Note</p>
                  <div className="text-sm bg-amber-50 rounded-md p-3 border border-amber-100 shadow-sm text-amber-900 italic font-medium">
                    "{selectedOrder.notes}"
                  </div>
                </div>
              )}

              {selectedOrder.assignment && (
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-widest font-semibold mb-2">Rider Assignment</p>
                  <div className="text-sm bg-indigo-50/50 rounded-md p-3 border border-indigo-100 shadow-sm space-y-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-indigo-900 flex items-center gap-1.5"><Bike className="w-4 h-4" /> {selectedOrder.assignment.rider?.name}</p>
                        <p className="text-xs text-indigo-700/80">{selectedOrder.assignment.rider?.phone}</p>
                      </div>
                      <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100 capitalize" variant="secondary">{selectedOrder.assignment.status.replace("_", " ")}</Badge>
                    </div>

                    {assignmentSubmitError && <div className="text-xs text-red-600 bg-red-50 p-2 border border-red-100 rounded">{assignmentSubmitError}</div>}

                    {["assigned", "accepted"].includes(selectedOrder.assignment.status) && (
                      <div className="bg-white p-3 rounded border border-indigo-100 space-y-2">
                        <p className="text-xs font-semibold text-indigo-900">Record Pickup</p>
                        <div className="flex gap-2">
                          <Input
                            placeholder="6-digit code"
                            className="h-8 text-xs"
                            value={pickupCode}
                            onChange={(e) => setPickupCode(e.target.value)}
                            maxLength={6}
                          />
                          <Button
                            size="sm"
                            className="h-8 shrink-0 bg-indigo-600 hover:bg-indigo-700 text-white"
                            onClick={() => handlePickup(selectedOrder.assignment.id)}
                            disabled={!pickupCode || assignmentSubmitting === "pickup"}
                          >
                            {assignmentSubmitting === "pickup" ? <Loader2 className="w-3 h-3 animate-spin" /> : "Verify & Pickup"}
                          </Button>
                        </div>
                      </div>
                    )}

                    {["picked_up", "out_for_delivery"].includes(selectedOrder.assignment.status) && (
                      <div className="bg-white p-3 rounded border border-indigo-100 space-y-2">
                        <p className="text-xs font-semibold text-indigo-900">Record Delivery</p>
                        <div className="space-y-2">
                          <Input
                            placeholder="6-digit delivery code"
                            className="h-8 text-xs"
                            value={deliveryCode}
                            onChange={(e) => setDeliveryCode(e.target.value)}
                            maxLength={6}
                          />
                          <Input
                            placeholder="Recipient Name"
                            className="h-8 text-xs"
                            value={recipientName}
                            onChange={(e) => setRecipientName(e.target.value)}
                          />
                          <Input
                            placeholder="Proof / Notes (e.g. Left at door)"
                            className="h-8 text-xs"
                            value={proofText}
                            onChange={(e) => setProofText(e.target.value)}
                          />
                          <Button
                            size="sm"
                            className="w-full h-8 bg-emerald-600 hover:bg-emerald-700 text-white"
                            onClick={() => handleDelivery(selectedOrder.assignment.id)}
                            disabled={!deliveryCode || !recipientName || !proofText || assignmentSubmitting === "delivery"}
                          >
                            {assignmentSubmitting === "delivery" ? <Loader2 className="w-3 h-3 animate-spin" /> : "Verify & Complete"}
                          </Button>
                        </div>
                      </div>
                    )}

                    <Button onClick={() => handleWhatsApp(selectedOrder.assignment.id)} size="sm" variant="outline" className="w-full gap-2 bg-white text-indigo-700 border-indigo-200 hover:bg-indigo-50">
                      <MessageCircle className="w-4 h-4 text-green-600" /> Send WhatsApp Instructions
                    </Button>
                  </div>
                </div>
              )}


              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-widest font-semibold mb-2">Order Items</p>
                <div className="border rounded-md divide-y text-sm shadow-sm bg-card">
                  {selectedOrder.items.map(item => (
                    <div key={item.id} className="p-3 flex justify-between items-center">
                      <div className="min-w-0 pr-4">
                        <p className="font-medium truncate">{item.name}</p>
                        <p className="text-xs text-muted-foreground font-medium mt-0.5">{formatCurrency(Number(item.price))} × {item.quantity}</p>
                      </div>
                      <p className="font-mono font-medium shrink-0">{formatCurrency(Number(item.price) * item.quantity)}</p>
                    </div>
                  ))}

                  {selectedOrder.merchandiseAmountKobo != null ? (
                    <>
                      <div className="p-3 flex justify-between items-center bg-slate-50/50 border-t border-dashed">
                        <p className="font-medium text-slate-500 text-xs">Subtotal</p>
                        <p className="font-mono font-medium text-sm">{formatCurrency(selectedOrder.merchandiseAmountKobo / 100)}</p>
                      </div>
                      {selectedOrder.deliveryFeeKobo != null && selectedOrder.deliveryFeeKobo > 0 && (
                        <div className="p-3 flex justify-between items-center bg-slate-50/50">
                          <p className="font-medium text-slate-500 text-xs">Delivery Fee</p>
                          <p className="font-mono font-medium text-sm">{formatCurrency(selectedOrder.deliveryFeeKobo / 100)}</p>
                        </div>
                      )}
                      <div className="p-3 flex justify-between items-center bg-slate-100">
                        <p className="font-medium text-slate-700">Total</p>
                        <p className="font-mono font-semibold text-lg">{formatCurrency(getOrderTotal(selectedOrder))}</p>
                      </div>
                    </>
                  ) : (
                    <div className="p-3 flex justify-between items-center bg-slate-50/50">
                      <p className="font-medium text-slate-600">Total</p>
                      <p className="font-mono font-semibold text-lg">{formatCurrency(getOrderTotal(selectedOrder))}</p>
                    </div>
                  )}

                  {selectedOrder.platformFeeKobo != null && selectedOrder.sellerNetKobo != null && (
                    <div className="border-t p-3 space-y-1 text-xs text-muted-foreground bg-white">
                      <div className="flex justify-between"><span>RetailPulse fee ({selectedOrder.platformFeeBps / 100}%)</span><span className="font-mono">{formatCurrency(selectedOrder.platformFeeKobo / 100)}</span></div>
                      <div className="flex justify-between font-medium text-slate-700"><span>Seller net</span><span className="font-mono">{formatCurrency(selectedOrder.sellerNetKobo / 100)}</span></div>
                    </div>
                  )}
                  {(selectedOrder.refunds ?? []).length > 0 && (
                    <div className="border-t p-3 space-y-2 text-xs">
                      <p className="font-semibold text-slate-700">Refunds</p>
                      {selectedOrder.refunds.map(refund => (
                        <div key={refund.id} className="rounded border bg-white p-2 space-y-1">
                          <div className="flex justify-between"><span className="capitalize">{refund.status} · {refund.source}</span><span className="font-mono">-{formatCurrency(refund.amountKobo / 100)}</span></div>
                          <div className="flex justify-between text-muted-foreground"><span>Fee reversed</span><span className="font-mono">{formatCurrency(refund.platformFeeKobo / 100)}</span></div>
                          <div className="flex justify-between text-muted-foreground"><span>Merchant share reversed</span><span className="font-mono">{formatCurrency(refund.merchantAmountKobo / 100)}</span></div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 border-t bg-card shrink-0 space-y-2">
            {["paid", "partially_refunded"].includes(selectedOrder.paymentStatus) && (
              <Button variant="outline" className="w-full border-red-200 text-red-700 hover:bg-red-50" disabled={!!updatingStatus} onClick={() => openRefund(selectedOrder)}>
                Refund items
              </Button>
            )}
            {selectedOrder.status === "pending_payment" && (
              <Button
                variant="destructive"
                className="w-full font-medium"
                disabled={!!updatingStatus}
                onClick={() => updateStatus(selectedOrder.id, "cancelled")}
              >
                {updatingStatus === "cancelled" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Cancel Order
              </Button>
            )}

            {selectedOrder.status === "paid" && (
              <Button
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium"
                disabled={!!updatingStatus}
                onClick={() => updateStatus(selectedOrder.id, "confirmed")}
              >
                {updatingStatus === "confirmed" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                Confirm Order
              </Button>
            )}

            {selectedOrder.status === "confirmed" && (
              <Button
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium"
                disabled={!!updatingStatus}
                onClick={() => updateStatus(selectedOrder.id, "preparing")}
              >
                {updatingStatus === "preparing" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Package className="w-4 h-4 mr-2" />}
                Start Preparing
              </Button>
            )}

            {selectedOrder.status === "preparing" && (
              <div className="flex gap-2">
                {selectedOrder.fulfillmentType === "pickup" ? (
                  <Button
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white font-medium"
                    disabled={!!updatingStatus}
                    onClick={() => updateStatus(selectedOrder.id, "ready_for_pickup")}
                  >
                    {updatingStatus === "ready_for_pickup" && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    Ready for Pickup
                  </Button>
                ) : (
                  selectedOrder.deliveryProvider !== "fez" || (!selectedFezBookingActive && !selectedFezBookingUnknown) ? (
                    <Button
                      className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium"
                      onClick={() => openAssignModal(selectedOrder)}
                    >
                      Assign Manual Rider & Dispatch
                    </Button>
                  ) : (
                    <p className="w-full rounded-md border bg-amber-50 p-3 text-xs text-amber-900">
                      {selectedFezBookingUnknown
                        ? "Manual dispatch is paused until the Fez booking outcome is reconciled."
                        : "A Fez booking is already on record. Do not dispatch a separate manual rider."}
                    </p>
                  )
                )}
              </div>
            )}

            {(selectedOrder.status === "ready_for_pickup" || (selectedOrder.status === "out_for_delivery" && !selectedOrder.assignment)) && (
              <Button
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium"
                disabled={!!updatingStatus}
                onClick={() => updateStatus(selectedOrder.id, "delivered")}
              >
                {updatingStatus === "delivered" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                Mark as Delivered
              </Button>
            )}

            {(selectedOrder.status === "delivered" || selectedOrder.status === "cancelled" || selectedOrder.status === "refunded") && (
              <div className="text-center py-2 bg-slate-50 border rounded-md">
                <p className="text-sm font-medium text-slate-600">
                  This order is <span className="capitalize">{selectedOrder.status.replace("_", " ")}</span>.
                </p>
              </div>
            )}

            {selectedOrder.trackingToken && (
              <Button variant="outline" className="w-full mt-3 gap-2 text-slate-600 font-medium bg-transparent border-dashed" asChild>
                <a href={`${BASE}/shop/track/${selectedOrder.trackingToken}`} target="_blank" rel="noreferrer">
                  <ExternalLink className="w-4 h-4" /> View Public Tracking Page
                </a>
              </Button>
            )}
          </div>
        </Card>
      )}

      <Dialog open={refundOpen} onOpenChange={setRefundOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Refund {selectedOrder?.orderNumber}</DialogTitle>
            <DialogDescription>Choose quantities. Amounts, discounts, fees, and merchant share use the original order snapshots.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              {selectedOrder?.items.map(item => {
                const remaining = item.quantity - refundedQuantity(selectedOrder, item.id);
                return <div key={item.id} className="flex items-center justify-between gap-4 rounded border p-3">
                  <div><p className="text-sm font-medium">{item.name}</p><p className="text-xs text-muted-foreground">{remaining} refundable</p></div>
                  <Input className="w-20" type="number" min={0} max={remaining} value={refundQuantities[item.id] ?? 0} onChange={e => setRefundQuantities(q => ({ ...q, [item.id]: Math.max(0, Math.min(remaining, Number(e.target.value) || 0)) }))} />
                </div>;
              })}
            </div>
            <Select value={refundSource} onValueChange={value => setRefundSource(value as "paystack" | "manual")}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="paystack">Initiate through Paystack</SelectItem><SelectItem value="manual">Record an existing Paystack refund</SelectItem></SelectContent>
            </Select>
            {refundSource === "manual" && <Input placeholder="Paystack refund ID" value={providerRefundId} onChange={e => setProviderRefundId(e.target.value)} />}
            <Textarea placeholder="Reason (optional)" value={refundReason} onChange={e => setRefundReason(e.target.value)} />
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={restoreInventory} onChange={e => setRestoreInventory(e.target.checked)} /> Return refunded items to available stock</label>
            {refundError && <p className="text-sm text-red-600">{refundError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundOpen(false)}>Cancel</Button>
            <Button variant="destructive" disabled={refunding || (refundSource === "manual" && !providerRefundId.trim())} onClick={submitRefund}>
              {refunding && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {refundSource === "manual" ? "Record refund" : "Start refund"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assignment Modal */}
      <Dialog open={!!assigningOrder} onOpenChange={(open) => {
        if (!open) {
          setAssigningOrder(null);
          // If we completed an assignment, refresh orders when closing modal
          if (assignmentResult) fetchOrders();
        }
      }}>
        <DialogContent className="sm:max-w-[450px]">
          {assignmentResult ? (
            <div className="py-6 space-y-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-6 h-6" />
                </div>
                <h2 className="text-xl font-bold tracking-tight">Rider Assigned Successfully</h2>
                <p className="text-sm text-muted-foreground mt-2">
                  Please securely copy the codes below. <br/>
                  <strong className="text-amber-600">They will only be shown this one time.</strong>
                </p>
              </div>

              <div className="bg-slate-50 border p-4 rounded-xl space-y-4">
                <div className="flex justify-between items-center pb-4 border-b border-slate-200">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase font-bold tracking-wider mb-1">Pickup Code</p>
                    <p className="text-3xl font-mono tracking-widest font-semibold">{assignmentResult.pickupCode}</p>
                  </div>
                  <Button size="icon" variant="outline" onClick={() => navigator.clipboard.writeText(assignmentResult.pickupCode)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase font-bold tracking-wider mb-1">Delivery Code</p>
                    <p className="text-3xl font-mono tracking-widest font-semibold text-primary">{assignmentResult.deliveryCode}</p>
                  </div>
                  <Button size="icon" variant="outline" onClick={() => navigator.clipboard.writeText(assignmentResult.deliveryCode)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="bg-amber-50 text-amber-800 p-3 rounded-md text-xs">
                Provide the <strong className="font-semibold">Pickup Code</strong> to the rider when they collect the package.
                Share the <strong className="font-semibold">Delivery Code</strong> with the customer to give to the rider upon delivery.
              </div>

              <DialogFooter>
                <Button className="w-full" onClick={() => { setAssigningOrder(null); fetchOrders(); }}>Done</Button>
              </DialogFooter>
            </div>
          ) : (
            <form onSubmit={handleAssign}>
              <DialogHeader>
                <DialogTitle>Assign Rider</DialogTitle>
                <DialogDescription>
                  Select a rider to deliver order #{assigningOrder?.orderNumber}
                </DialogDescription>
              </DialogHeader>
              <div className="py-6 space-y-4">
                {assignmentError && <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-md">{assignmentError}</div>}

                <div className="space-y-2">
                  <label className="text-sm font-medium">Available Riders</label>
                  {riders.length === 0 ? (
                    <div className="text-sm text-amber-600 bg-amber-50 p-3 rounded-md border border-amber-100">
                      No active riders found. Please add a rider in the Logistics tab first.
                    </div>
                  ) : (
                    <Select value={selectedRiderId} onValueChange={setSelectedRiderId} required>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a rider..." />
                      </SelectTrigger>
                      <SelectContent>
                        {riders.map((r: any) => (
                          <SelectItem key={r.id} value={String(r.id)}>
                            {r.name} ({r.phone})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setAssigningOrder(null)}>Cancel</Button>
                <Button type="submit" disabled={!selectedRiderId || assignmentLoading}>
                  {assignmentLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Assign & Generate Codes
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DiscountsTab() {
  const [codes, setCodes] = useState<any[]>([]);
  const [form, setForm] = useState({ code: "", discountType: "percentage", value: "", maxUses: "", expiresAt: "" });
  const [loading, setLoading] = useState(true); const [saving, setSaving] = useState(false); const [error, setError] = useState("");
  const load = useCallback(async () => { const r = await fetch(`${BASE}/api/ecommerce/discount-codes`, { credentials: "include" }); if (r.ok) setCodes(await r.json()); setLoading(false); }, []);
  useEffect(() => { load(); }, [load]);
  const save = async (e: React.FormEvent) => { e.preventDefault(); setSaving(true); setError(""); try { const r = await fetch(`${BASE}/api/ecommerce/discount-codes`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...form, value: Number(form.value), maxUses: form.maxUses ? Number(form.maxUses) : null, expiresAt: form.expiresAt || null }) }); const d = await r.json(); if (!r.ok) { setError(d.error || "Could not create code"); return; } setForm({ code: "", discountType: "percentage", value: "", maxUses: "", expiresAt: "" }); load(); } catch { setError("Network error"); } finally { setSaving(false); } };
  const toggle = async (c: any) => { await fetch(`${BASE}/api/ecommerce/discount-codes/${c.id}`, { method: "PATCH", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ isActive: !c.isActive }) }); load(); };
  const remove = async (id: number) => { if (!confirm("Delete this discount code?")) return; await fetch(`${BASE}/api/ecommerce/discount-codes/${id}`, { method: "DELETE", credentials: "include" }); load(); };
  if (loading) return <div className="py-20 flex justify-center"><Loader2 className="animate-spin" /></div>;
  return <div className="grid lg:grid-cols-[340px_1fr] gap-6 max-w-5xl"><Card className="shadow-none"><CardHeader><CardTitle className="text-lg">New discount code</CardTitle><CardDescription>Apply a percentage or fixed naira reduction at checkout.</CardDescription></CardHeader><CardContent><form onSubmit={save} className="space-y-4"><Input placeholder="CODE10" value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })} required data-testid="input-discount-code" /><div className="grid grid-cols-2 gap-2"><Select value={form.discountType} onValueChange={v => setForm({ ...form, discountType: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="percentage">Percentage</SelectItem><SelectItem value="fixed">Fixed amount</SelectItem></SelectContent></Select><Input type="number" min="0" step="0.01" placeholder="Value" value={form.value} onChange={e => setForm({ ...form, value: e.target.value })} required data-testid="input-discount-value" /></div><Input type="number" min="1" placeholder="Maximum uses (optional)" value={form.maxUses} onChange={e => setForm({ ...form, maxUses: e.target.value })} /><Input type="date" value={form.expiresAt} onChange={e => setForm({ ...form, expiresAt: e.target.value })} />{error && <p className="text-sm text-red-600">{error}</p>}<Button type="submit" disabled={saving} className="w-full" data-testid="button-create-discount">{saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Create code</Button></form></CardContent></Card><Card className="shadow-none"><CardHeader><CardTitle className="text-lg">Your codes</CardTitle></CardHeader><CardContent>{codes.length === 0 ? <p className="text-sm text-muted-foreground py-8 text-center">No discount codes yet.</p> : <div className="space-y-2">{codes.map(c => <div key={c.id} className="flex items-center justify-between border rounded-md p-3"><div><p className="font-mono font-semibold">{c.code}</p><p className="text-xs text-muted-foreground">{c.discountType === "percentage" ? `${c.value}% off` : `${formatCurrency(Number(c.value))} off`} · {c.usedCount || 0}{c.maxUses ? `/${c.maxUses}` : ""} used</p></div><div className="flex gap-2"><Button size="sm" variant="outline" onClick={() => toggle(c)} data-testid={`toggle-discount-${c.id}`}>{c.isActive ? "Active" : "Paused"}</Button><Button size="icon" variant="ghost" className="text-red-600" onClick={() => remove(c.id)} data-testid={`delete-discount-${c.id}`}><Trash2 className="w-4 h-4" /></Button></div></div>)}</div>}</CardContent></Card></div>;
}

function AnalyticsTab() {
  const [stats, setStats] = useState<any>(null); const [loading, setLoading] = useState(true);
  useEffect(() => { fetch(`${BASE}/api/ecommerce/my-stats`, { credentials: "include" }).then(r => r.ok ? r.json() : null).then(setStats).finally(() => setLoading(false)); }, []);
  if (loading) return <div className="py-20 flex justify-center"><Loader2 className="animate-spin" /></div>;
  const entries = stats ? Object.entries(stats).filter(([_, v]) => typeof v === "number" || typeof v === "string") : [];
  return <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">{entries.length ? entries.map(([key, value]) => <Card key={key} className="shadow-none"><CardContent className="p-5"><p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">{key.replace(/([A-Z])/g, " $1").replace(/_/g, " ")}</p><p className="text-2xl font-semibold mt-2">{key.toLowerCase().includes("revenue") || key.toLowerCase().includes("sales") ? formatCurrency(Number(value)) : String(value)}</p></CardContent></Card>) : <Card className="shadow-none col-span-full"><CardContent className="p-10 text-center text-sm text-muted-foreground">Analytics will appear after your first online order.</CardContent></Card>}</div>;
}

function LogisticsTab() {
  const [zones, setZones] = useState<any[]>([]);
  const [riders, setRiders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("zones");

  const [zoneModal, setZoneModal] = useState(false);
  const [editingZone, setEditingZone] = useState<any>(null);
  const [zoneForm, setZoneForm] = useState({ name: "", description: "", rateKobo: "", active: true });

  const [riderModal, setRiderModal] = useState(false);
  const [editingRider, setEditingRider] = useState<any>(null);
  const [riderForm, setRiderForm] = useState({ name: "", phone: "", riderType: "external", vehicleType: "motorcycle", notes: "", active: true });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const [zRes, rRes] = await Promise.all([
        fetch(`${BASE}/api/ecommerce/delivery-zones`, { credentials: "include" }),
        fetch(`${BASE}/api/ecommerce/riders`, { credentials: "include" })
      ]);
      if (zRes.ok) setZones(await zRes.json());
      if (rRes.ok) setRiders(await rRes.json());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const saveZone = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      const url = editingZone ? `${BASE}/api/ecommerce/delivery-zones/${editingZone.id}` : `${BASE}/api/ecommerce/delivery-zones`;
      const method = editingZone ? "PATCH" : "POST";
      const res = await fetch(url, {
        method, credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...zoneForm, rateKobo: Math.round(Number(zoneForm.rateKobo) * 100) })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to save zone");
      setZoneModal(false);
      fetchData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteZone = async (id: number) => {
    if (!confirm("Delete this delivery zone?")) return;
    await fetch(`${BASE}/api/ecommerce/delivery-zones/${id}`, { method: "DELETE", credentials: "include" });
    fetchData();
  };

  const saveRider = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      const url = editingRider ? `${BASE}/api/ecommerce/riders/${editingRider.id}` : `${BASE}/api/ecommerce/riders`;
      const method = editingRider ? "PATCH" : "POST";
      const res = await fetch(url, {
        method, credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(riderForm)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to save rider");
      setRiderModal(false);
      fetchData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteRider = async (id: number) => {
    if (!confirm("Delete this rider?")) return;
    await fetch(`${BASE}/api/ecommerce/riders/${id}`, { method: "DELETE", credentials: "include" });
    fetchData();
  };

  const openZoneAdd = () => { setEditingZone(null); setZoneForm({ name: "", description: "", rateKobo: "", active: true }); setError(""); setZoneModal(true); };
  const openZoneEdit = (z: any) => { setEditingZone(z); setZoneForm({ name: z.name, description: z.description || "", rateKobo: String(z.rateKobo / 100), active: z.active }); setError(""); setZoneModal(true); };

  const openRiderAdd = () => { setEditingRider(null); setRiderForm({ name: "", phone: "", riderType: "external", vehicleType: "motorcycle", notes: "", active: true }); setError(""); setRiderModal(true); };
  const openRiderEdit = (r: any) => { setEditingRider(r); setRiderForm({ name: r.name, phone: r.phone, riderType: r.riderType, vehicleType: r.vehicleType || "motorcycle", notes: r.notes || "", active: r.active }); setError(""); setRiderModal(true); };

  if (loading) return <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex justify-between items-end">
        <div>
          <h2 className="text-xl font-bold tracking-tight">Logistics Management</h2>
          <p className="text-sm text-muted-foreground mt-1">Manage delivery areas, rates, and rider contacts.</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full sm:w-[400px] grid-cols-2">
          <TabsTrigger value="zones">Delivery Zones</TabsTrigger>
          <TabsTrigger value="riders">Rider Contacts</TabsTrigger>
        </TabsList>

        <TabsContent value="zones" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Button onClick={openZoneAdd}><Plus className="w-4 h-4 mr-2" /> Add Zone</Button>
          </div>
          {zones.length === 0 ? (
            <Card className="border-dashed shadow-none bg-muted/10">
              <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                <MapIcon className="w-12 h-12 text-slate-300 mb-4" />
                <p className="font-medium text-slate-900 mb-1">No Delivery Zones</p>
                <p className="text-sm text-slate-500 max-w-sm mb-6">Create zones (e.g. "Lagos Island", "Mainland") to set delivery rates for customers.</p>
                <Button variant="outline" onClick={openZoneAdd}><Plus className="w-4 h-4 mr-2" /> Create First Zone</Button>
              </CardContent>
            </Card>
          ) : (
            <>
            <div className="hidden overflow-x-auto rounded-md border bg-card md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Zone Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Rate</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[80px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {zones.map(z => (
                    <TableRow key={z.id}>
                      <TableCell className="font-medium">{z.name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{z.description || "—"}</TableCell>
                      <TableCell className="font-mono">{formatCurrency(z.rateKobo / 100)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={z.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-50 text-slate-500"}>{z.active ? "Active" : "Inactive"}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500 hover:text-slate-900" onClick={() => openZoneEdit(z)}><Pencil className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500 hover:text-red-600 hover:bg-red-50" onClick={() => deleteZone(z.id)}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="space-y-3 md:hidden">
              {zones.map(zone => (
                <Card key={zone.id} className="shadow-none"><CardContent className="flex items-start justify-between gap-3 p-4">
                  <div className="min-w-0"><p className="font-semibold">{zone.name}</p><p className="mt-1 text-sm text-muted-foreground">{zone.description || "No description"}</p><p className="mt-2 font-mono text-sm">{formatCurrency(zone.rateKobo / 100)}</p><Badge variant="outline" className={`mt-2 ${zone.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-50 text-slate-500"}`}>{zone.active ? "Active" : "Inactive"}</Badge></div>
                  <div className="flex shrink-0"><Button type="button" variant="ghost" size="icon" aria-label={`Edit ${zone.name}`} onClick={() => openZoneEdit(zone)}><Pencil className="h-4 w-4" /></Button><Button type="button" variant="ghost" size="icon" aria-label={`Delete ${zone.name}`} className="text-red-600" onClick={() => deleteZone(zone.id)}><Trash2 className="h-4 w-4" /></Button></div>
                </CardContent></Card>
              ))}
            </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="riders" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Button onClick={openRiderAdd}><Plus className="w-4 h-4 mr-2" /> Add Rider</Button>
          </div>
          {riders.length === 0 ? (
            <Card className="border-dashed shadow-none bg-muted/10">
              <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                <Bike className="w-12 h-12 text-slate-300 mb-4" />
                <p className="font-medium text-slate-900 mb-1">No Rider Contacts</p>
                <p className="text-sm text-slate-500 max-w-sm mb-6">Add internal drivers or external logistics partners so you can assign them to delivery orders.</p>
                <Button variant="outline" onClick={openRiderAdd}><Plus className="w-4 h-4 mr-2" /> Add First Rider</Button>
              </CardContent>
            </Card>
          ) : (
            <>
            <div className="hidden overflow-x-auto rounded-md border bg-card md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Rider / Partner</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Type & Vehicle</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[80px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {riders.map(r => (
                    <TableRow key={r.id}>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell className="text-sm font-mono text-slate-600">{r.phone}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="capitalize bg-slate-50">{r.riderType}</Badge>
                          <span className="text-xs text-muted-foreground capitalize flex items-center gap-1">
                            {r.vehicleType === 'motorcycle' ? <Bike className="w-3 h-3"/> : <Truck className="w-3 h-3"/>}
                            {r.vehicleType}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={r.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-50 text-slate-500"}>{r.active ? "Active" : "Inactive"}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500 hover:text-slate-900" onClick={() => openRiderEdit(r)}><Pencil className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500 hover:text-red-600 hover:bg-red-50" onClick={() => deleteRider(r.id)}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="space-y-3 md:hidden">
              {riders.map(rider => (
                <Card key={rider.id} className="shadow-none"><CardContent className="flex items-start justify-between gap-3 p-4">
                  <div className="min-w-0"><p className="font-semibold">{rider.name}</p><p className="mt-1 break-all font-mono text-sm text-muted-foreground">{rider.phone}</p><div className="mt-2 flex flex-wrap items-center gap-2"><Badge variant="outline" className="capitalize">{rider.riderType}</Badge><span className="text-xs capitalize text-muted-foreground">{rider.vehicleType}</span><Badge variant="outline" className={rider.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-50 text-slate-500"}>{rider.active ? "Active" : "Inactive"}</Badge></div></div>
                  <div className="flex shrink-0"><Button type="button" variant="ghost" size="icon" aria-label={`Edit ${rider.name}`} onClick={() => openRiderEdit(rider)}><Pencil className="h-4 w-4" /></Button><Button type="button" variant="ghost" size="icon" aria-label={`Delete ${rider.name}`} className="text-red-600" onClick={() => deleteRider(rider.id)}><Trash2 className="h-4 w-4" /></Button></div>
                </CardContent></Card>
              ))}
            </div>
            </>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={zoneModal} onOpenChange={setZoneModal}>
        <DialogContent>
          <form onSubmit={saveZone}>
            <DialogHeader><DialogTitle>{editingZone ? "Edit Delivery Zone" : "New Delivery Zone"}</DialogTitle></DialogHeader>
            <div className="space-y-4 py-4">
              {error && <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-md">{error}</div>}
              <div className="space-y-2">
                <label className="text-sm font-medium">Zone Name</label>
                <Input value={zoneForm.name} onChange={e => setZoneForm(p => ({ ...p, name: e.target.value }))} required placeholder="e.g. Lagos Island" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Description (optional)</label>
                <Input value={zoneForm.description} onChange={e => setZoneForm(p => ({ ...p, description: e.target.value }))} placeholder="e.g. Lekki, Ikoyi, Victoria Island" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Delivery Rate (₦)</label>
                <Input type="number" step="0.01" min="0" value={zoneForm.rateKobo} onChange={e => setZoneForm(p => ({ ...p, rateKobo: e.target.value }))} required placeholder="1500.00" />
              </div>
              <div className="flex items-center gap-2 pt-2">
                <input type="checkbox" id="zone-active" checked={zoneForm.active} onChange={e => setZoneForm(p => ({ ...p, active: e.target.checked }))} className="rounded border-input text-primary focus:ring-primary" />
                <label htmlFor="zone-active" className="text-sm">Zone is active and available to customers</label>
              </div>
            </div>
            <DialogFooter><Button type="submit" disabled={saving}>{saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Save Zone</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={riderModal} onOpenChange={setRiderModal}>
        <DialogContent>
          <form onSubmit={saveRider}>
            <DialogHeader><DialogTitle>{editingRider ? "Edit Rider / Partner" : "New Rider / Partner"}</DialogTitle></DialogHeader>
            <div className="space-y-4 py-4">
              {error && <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-md">{error}</div>}
              <div className="space-y-2">
                <label className="text-sm font-medium">Name / Company</label>
                <Input value={riderForm.name} onChange={e => setRiderForm(p => ({ ...p, name: e.target.value }))} required placeholder="e.g. John Doe or Gokada" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Phone Number</label>
                <Input type="tel" value={riderForm.phone} onChange={e => setRiderForm(p => ({ ...p, phone: e.target.value }))} required placeholder="e.g. 08012345678" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Type</label>
                  <Select value={riderForm.riderType} onValueChange={v => setRiderForm(p => ({ ...p, riderType: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="internal">Internal (Staff)</SelectItem>
                      <SelectItem value="external">External (Partner)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Vehicle</label>
                  <Select value={riderForm.vehicleType} onValueChange={v => setRiderForm(p => ({ ...p, vehicleType: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="motorcycle">Motorcycle</SelectItem>
                      <SelectItem value="car">Car</SelectItem>
                      <SelectItem value="van">Van / Truck</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Notes (optional)</label>
                <Textarea value={riderForm.notes} onChange={e => setRiderForm(p => ({ ...p, notes: e.target.value }))} placeholder="Service areas, limitations..." className="h-20" />
              </div>
              <div className="flex items-center gap-2 pt-2">
                <input type="checkbox" id="rider-active" checked={riderForm.active} onChange={e => setRiderForm(p => ({ ...p, active: e.target.checked }))} className="rounded border-input text-primary focus:ring-primary" />
                <label htmlFor="rider-active" className="text-sm">Rider is active and available for assignments</label>
              </div>
            </div>
            <DialogFooter><Button type="submit" disabled={saving}>{saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Save Rider</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function EcommercePage() {
  const { user } = useAuth();
  const [activeSection, setActiveSection] = useState("orders");

  if (user?.role !== "admin" && user?.role !== "superadmin") {
    return (
      <div className="p-8 max-w-2xl mx-auto text-center">
        <h2 className="text-2xl font-semibold mb-2">Access Denied</h2>
        <p className="text-muted-foreground">Only store administrators can manage the online store.</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-[1600px] mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Online Store</h1>
        <p className="text-muted-foreground mt-1">Manage your storefront, products, and online orders.</p>
      </div>

      <Tabs value={activeSection} onValueChange={setActiveSection} className="w-full">
        <div className="mb-6">
          <div className="sm:hidden">
            <label htmlFor="ecommerce-section-select" className="sr-only">Ecommerce section</label>
            <Select value={activeSection} onValueChange={setActiveSection}>
              <SelectTrigger id="ecommerce-section-select" aria-label="Choose ecommerce section" className="w-full bg-background">
                <SelectValue placeholder="Choose section" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="orders">Orders</SelectItem>
                <SelectItem value="products">Products</SelectItem>
                <SelectItem value="discounts">Discounts</SelectItem>
                <SelectItem value="logistics">Logistics</SelectItem>
                <SelectItem value="finance">Finance</SelectItem>
                <SelectItem value="analytics">Analytics</SelectItem>
                <SelectItem value="settings">Settings</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="hidden w-full overflow-x-auto no-scrollbar sm:block">
          <TabsList className="grid w-full grid-cols-7 bg-muted/50 p-1">
            <TabsTrigger value="orders" className="font-medium">Orders</TabsTrigger>
            <TabsTrigger value="products" className="font-medium">Products</TabsTrigger>
            <TabsTrigger value="discounts" className="font-medium">Discounts</TabsTrigger>
            <TabsTrigger value="logistics" className="font-medium">Logistics</TabsTrigger>
            <TabsTrigger value="finance" className="font-medium">Finance</TabsTrigger>
            <TabsTrigger value="analytics" className="font-medium">Analytics</TabsTrigger>
            <TabsTrigger value="settings" className="font-medium">Settings</TabsTrigger>
          </TabsList>
          </div>
        </div>
        <TabsContent value="orders" className="mt-0 outline-none">
          <OrdersTab />
        </TabsContent>
        <TabsContent value="products" className="mt-0 outline-none">
          <ProductsTab />
        </TabsContent>
        <TabsContent value="discounts" className="mt-0 outline-none">
          <DiscountsTab />
        </TabsContent>
        <TabsContent value="logistics" className="mt-0 outline-none">
          <LogisticsTab />
        </TabsContent>
        <TabsContent value="finance" className="mt-0 outline-none">
          <FinanceTab />
        </TabsContent>
        <TabsContent value="analytics" className="mt-0 outline-none">
          <AnalyticsTab />
        </TabsContent>
        <TabsContent value="settings" className="mt-0 outline-none">
          <StoreSetupTab onSaved={() => {}} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
